<template>
  <div v-if="!loading">
    <template v-if="hasPerm('gallery.edit_price_list')">
      <CContainer>
        <CRow>
          <CCol>
            <div class="position-relative"
                 v-if="showAdvancedFlag">
              <div class="d-flex align-items-end justify-content-end align-items-center event-wizard-adv">
                <label class="m-0">{{ $pgettext('event_template.label', 'Advanced') }}</label>
                <CSwitch class="ml-2 mt-2" color="primary"
                         variant="3d" size="sm"
                         :checked="advanced"
                         @update:checked="setAdvanced({value:!advanced,type:'price_list'})"
                />
              </div>
            </div>
          </CCol>
        </CRow>

        <template v-if="advanced">
          <CRow class="">
            <CCol lg="8" class="mb-3">
              <p class="text-help-dark" v-translate translate-context="event_wizard.subtitle">
                Create one or more price list for this event</p>
              <CButton color="primary" @click="addPriceList">
                <CIcon name="cipPlus"/>
                <translate translate-context="pricelist.actions">Add Price List</translate>
              </CButton>
            </CCol>
          </CRow>
        </template>
        <template v-if="multiplePriceList">
          <CRow style="padding-bottom: 140px">
            <CCol style="min-width: 0;">
              <draggable v-model="priceLists" handle=".handle" ghost-class="ghost">
                <transition-group type="transition" :name="'flip-list'">
                  <div class="accordion-list__item" v-for="priceList in priceLists" :key="priceList.id">
                    <PriceListHeader
                      :data="priceList"
                      :active="activePriceList === priceList.id"
                      :advanced="advanced"
                      @delete="onDeletePriceList(priceList)"
                      @edit="onEditPriceList(priceList)"
                    />
                    <PriceListOpened
                      ref="priceList"
                      :data="priceList"
                      :isSubGallery="isSubGallery"
                      :advanced="advanced"
                      v-if="activePriceList === priceList.id"
                      @preview="onPreviewPriceList"
                      @dirty="onDirty"
                    />
                  </div>
                </transition-group>
              </draggable>
            </CCol>
          </CRow>
        </template>
        <template v-else>
          <PriceListOpened
            :data="priceLists[0]"
            :isSubGallery="isSubGallery"
            :advanced="advanced"
            @preview="onPreviewPriceList"
            @dirty="onDirty"
            ref="priceList"
          />
        </template>
      </CContainer>
      <TabFooter @save="onSave" @reset="onReset" :saveDisabled="!isDirty"/>
    </template>
    <template v-else>
      You can preview how the price list will look like for the end user using the right preview panel
    </template>
  </div>
</template>

<script>
import {v4 as uuid} from "uuid";
import {cloneDeep} from "lodash";
import draggable from 'vuedraggable'
import PriceListOpened from "@/domain/eventGallery/tab/priceList/PriceListOpened.vue";
import TabFooter from "@/domain/eventGallery/components/TabFooter.vue";
import PriceListHeader from "@/domain/eventGallery/tab/priceList/PriceListHeader.vue";
import {mapActions, mapGetters} from "vuex";
import eventBus from "@/domain/core/constant/eventBus";
import Vue from "vue";

export default {
  name: "TabPriceList",
  components: {PriceListHeader, TabFooter, PriceListOpened, draggable},
  emits: ['save', 'preview'],
  props: {
    isSubGallery: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      loading: true,
      isDirty: false,
      priceLists: [],
      activePriceList: null,
      newPriceList: {
        valid_from: '',
        valid_to: '',
        configuration: {},
        items: [],
        allow_preorder: false,
        allow_upsell: false,
        cta_enabled: false,
        cta_min_qty: 0,
        cta_position: 'b',
      }
    }
  },
  async mounted() {
    await this.fetchTabData('price_list')
    this.onReset()
    this.loading = false
  },
  computed: {
    ...mapGetters('event', [
      'eventCurrency',
      'currentEventName',
    ]),
    ...mapGetters('eventGallery', [
      'step_price_list'
    ]),
    ...mapGetters('eventWizard', {
      advancedFn: 'advanced',
    }),
    advanced() {
      return this.advancedFn('price_list')
    },
    showAdvancedFlag() {
      return this.hasPerm('advanced.price_list')
    },
    multiplePriceList() {
      if (this.advanced) return true
      return this.priceLists && this.priceLists.length > 1
    }
  },
  methods: {
    ...mapActions('eventWizard', [
      'fetchEventConfiguration',
      'setAdvanced',
    ]),
    ...mapActions('eventGallery', [
      'fetchTabData'
    ]),
    onDirty(dirty) {
      this.isDirty = dirty
    },
    onReset() {
      this.loading = true
      if (!this.step_price_list.price_lists)
        this.priceLists = []
      else
        this.priceLists = cloneDeep(this.step_price_list.price_lists).sort((a, b) => a.order - b.order)
      if (this.priceLists.length <= 0)
        this.addPriceList()
      if (!this.activePriceList || !this.priceLists.find(p => p.id === this.activePriceList))
        this.activePriceList = this.priceLists[0].id
      this.loading = false
      this.updatePreview()
    },
    addPriceList() {
      this.priceLists.push({
        id: uuid(),
        isNew: true,
        order: this.priceLists.length + 1,
        ...this.newPriceList
      })
      this.activePriceList = this.priceLists[this.priceLists.length - 1].id
      this.updatePreview()
    },
    onDeletePriceList(priceList) {
      this.priceLists = this.priceLists.filter(p => p.id !== priceList.id)
    },
    onEditPriceList(priceList) {
      if (this.activePriceList === priceList.id)
        this.activePriceList = null
      else
        this.activePriceList = priceList.id
      this.updatePreview()
    },
    async onSave() {
      const priceListRef = this.multiplePriceList ? this.$refs.priceList?.[0] : this.$refs.priceList
      if (priceListRef && !priceListRef.isValid()) {
        await this.notifyError(this.$pgettext('event_gallery.price_list', 'Please set a price before saving'))
        return
      }
      this.updateSorting()
      this.forceSavePriceList()
      await this.emitPromised('save', {
        price_lists: this.priceLists.map(priceList => {
          // The id is valid and should be kept only if it's a "valid" id, and not a "fake" uuid used only for differentiate element in the loop
          if (!priceList.valid_from) priceList.valid_from = null
          if (!priceList.valid_to) priceList.valid_to = null
          return {
            ...priceList,
            id: priceList.isNew ? null : priceList.id,
            items: priceList.items ?? [],
          }
        }) // TO REVIEW -> not works with PACK  .filter(priceList => priceList.items.length >= 0)
      })
      await this.fetchEventConfiguration()
      await this.emitEvent(eventBus.event.publish_data_changed)
      this.onReset()
    },
    forceSavePriceList() {
      let price = null
      for (let i = 0; i < this.priceLists.length; i++) {
        if (!this.priceLists[i]) continue
        if (this.multiplePriceList)
          price = this.$refs.priceList[0].getPriceList()
        else
          price = this.$refs.priceList.getPriceList()
      }

      this.priceLists = this.priceLists.map(p => {
        if (p.id === this.activePriceList)
          return price
        return p
      })
    },
    onPreviewPriceList(priceList) {
      const preview = {
        items: priceList.items,
        currency: this.eventCurrency,
        event_name: this.currentEventName,
        lang: Vue.config.language,
      }
      if (priceList.isDemo) {
        preview.isDemo = true
        preview.configuration = priceList.configuration
      }
      this.$emit('preview', preview)
    },
    updateSorting() {
      this.priceLists = this.priceLists.map((priceList, index) => {
        priceList.order = index
        return priceList
      })
    },
    updatePreview() {
      const active = this.priceLists.find(p => p.id === this.activePriceList)
      if (!active) return this.$emit('preview', {})
      this.$emit('preview', {
        items: active.items,
        currency: this.eventCurrency,
        event_name: this.currentEventName,
        lang: Vue.config.language
      })
    }
  }
}
</script>
